import { useEffect, useState } from 'react';
import {
  Box, Paper, Stepper, Step, StepLabel, Button, Typography,
  Select, MenuItem, FormControl, InputLabel, Checkbox,
  Autocomplete, TextField, Alert,
  FormControlLabel, Stack,
} from '@mui/material';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import { AWS_REGIONS } from '../../../constants/awsRegions';
import { enableGuardDuty, getGuardDutyConfig, resolveMemberEmails } from '../../../services/guarddutyApi';
import { listSecurityHubAccounts } from '../../../services/securityhubApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading } from '../../../components/portal/PortalStates';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import MultiSelectField from './MultiSelectField';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, sectionCardSx, sectionHeadingSx, insetPanelSx, emptyTextSx, actionRowSx,
} from '../portalStyles';

const steps = ['Admin account', 'Regions', 'Features', 'Members', 'Emails', 'Review'];

// GuardDuty accent, shared with every other GuardDuty route.
const GD = '#E65100';
const GD_DARK = '#BF360C';

const primaryBtnSx = {
  backgroundColor: GD,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
  px: 3,
  '&:hover': { backgroundColor: GD_DARK },
};

const textBtnSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
  color: CK.body,
};

// Step bodies sit at the card's own p:3 edge — only vertical breathing room is
// added here so the content lines up with the Stepper and the footer buttons.
const stepBodySx = { py: 1 };

const bodyTextSx = { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body };

const checkboxSx = { color: CK.disabled, '&.Mui-checked': { color: GD } };

const featureLabelSx = {
  display: 'block',
  '& .MuiFormControlLabel-label': { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.navy },
};

// GuardDuty protection plans. All default-ON; the raw enum names are what the
// backend EnableGuardDutyRequest.features expects.
const FEATURE_OPTIONS = [
  { id: 'S3_DATA_EVENTS', label: 'S3 Protection (data events)' },
  { id: 'EKS_AUDIT_LOGS', label: 'EKS Protection (audit logs)' },
  { id: 'EBS_MALWARE_PROTECTION', label: 'Malware Protection (EBS)' },
  { id: 'RDS_LOGIN_EVENTS', label: 'RDS Protection (login events)' },
  { id: 'LAMBDA_NETWORK_LOGS', label: 'Lambda Protection (network logs)' },
  { id: 'RUNTIME_MONITORING', label: 'Runtime Monitoring' },
];

// ReviewRow renders one label/value line of the Review step.
function ReviewRow({ label, value }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.25, sm: 2 }}
      sx={{ py: 0.5, alignItems: { xs: 'flex-start', sm: 'baseline' } }}
    >
      <Typography sx={{ ...bodyTextSx, color: CK.muted, minWidth: { xs: 0, sm: 170 } }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: CK.navy,
          wordBreak: 'break-word',
          minWidth: 0,
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export default function GuardDutySetup({ onComplete, priorFailure }) {
  const [active, setActive] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [adminAccountId, setAdminAccountId] = useState('');
  const [homeRegion, setHomeRegion] = useState('us-east-1');
  const [linkingRegions, setLinkingRegions] = useState(['us-east-1']);
  const [features, setFeatures] = useState(FEATURE_OPTIONS.map((f) => f.id));
  const [memberAccountIds, setMemberAccountIds] = useState([]);

  // Member-email resolution state (Emails step).
  const [emailRows, setEmailRows] = useState([]); // [{accountId, email, resolved, reason}]
  const [manualEmails, setManualEmails] = useState({}); // accountId -> typed root email
  const [resolvingEmails, setResolvingEmails] = useState(false);

  // In-progress state (after submit)
  const [config, setConfig] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const acctRes = await listSecurityHubAccounts();
        setAccounts(acctRes?.data?.accounts || acctRes?.data || []);
      } catch (e) {
        setError(e.message);
      }
    })();
  }, []);

  // Poll while in_progress.
  useEffect(() => {
    if (!config || config.status !== 'in_progress') return undefined;
    const id = setInterval(async () => {
      try {
        const res = await getGuardDutyConfig();
        const next = res?.data;
        if (next) setConfig(next);
        if (next && next.status !== 'in_progress') {
          clearInterval(id);
          if (onComplete) onComplete();
        }
      } catch (_) { /* swallow transient */ }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [config, onComplete]);

  const next = () => setActive((s) => s + 1);
  const back = () => setActive((s) => s - 1);

  // Resolve member root emails when entering the Emails step or when the
  // member selection changes while on it. Auto-resolved accounts are read-only;
  // failures require a manual entry.
  useEffect(() => {
    if (active !== 4) return;
    let cancelled = false;
    (async () => {
      setResolvingEmails(true);
      try {
        const res = await resolveMemberEmails(memberAccountIds);
        const results = res?.data?.results || [];
        if (cancelled) return;
        setEmailRows(results);
        // Drop manual entries for accounts no longer selected / now auto-resolved.
        setManualEmails((cur) => {
          const next2 = {};
          results.forEach((row) => {
            if (!row.resolved && cur[row.accountId]) next2[row.accountId] = cur[row.accountId];
          });
          return next2;
        });
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setResolvingEmails(false);
      }
    })();
    return () => { cancelled = true; };
  }, [active, memberAccountIds]);

  const emailsResolved = emailRows.length === memberAccountIds.length
    && emailRows.every((row) => row.resolved || (manualEmails[row.accountId] || '').trim() !== '');

  const canNext = () => {
    if (active === 0) return !!adminAccountId;
    if (active === 1) return !!homeRegion && linkingRegions.length > 0 && linkingRegions.includes(homeRegion);
    if (active === 4) return !resolvingEmails && emailsResolved;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Only send the emails the user actually typed; auto-resolved accounts
      // are re-resolved server-side.
      const memberEmails = {};
      Object.entries(manualEmails).forEach(([acctId, val]) => {
        const v = (val || '').trim();
        if (v) memberEmails[acctId] = v;
      });
      const res = await enableGuardDuty({
        adminAccountId,
        homeRegion,
        linkingRegions,
        memberAccountIds,
        memberEmails,
        features,
      });
      setConfig(res?.data);
    } catch (e) {
      // A 422 means some accounts still need an email — send the user back to
      // the Emails step with the inline error.
      setError(e.message);
      setActive(4);
    } finally {
      setSubmitting(false);
    }
  };

  const body = () => {
    if (config) {
      const statusLabel = {
        in_progress: 'Setting up GuardDuty…',
        enabled: 'GuardDuty is enabled',
        partial_failure: 'GuardDuty enabled with partial failures',
        failed: 'Setup failed',
        disabled: 'GuardDuty is disabled',
      }[config.status] || config.status;
      return (
        <Box sx={stepBodySx}>
          <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>{statusLabel}</Typography>
          {config.status === 'in_progress' && (
            <>
              <Typography sx={{ ...bodyTextSx, mt: 1 }}>
                Enrolling member accounts, enabling protection plans, and
                configuring the delegated administrator. This can take a few minutes.
              </Typography>
              <OperationProgress
                percent={config.progress}
                label={config.progressLabel || 'Working…'}
                sx={{ my: 2 }}
              />
            </>
          )}
          {config.errorMessage && (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: '8px' }}>{config.errorMessage}</Alert>
          )}
          <Box sx={{ ...insetPanelSx, mt: 2 }}>
            <ReviewRow label="Admin account" value={config.adminAccountId} />
            <ReviewRow label="Home region" value={config.homeRegion} />
          </Box>
        </Box>
      );
    }

    if (active === 0) {
      return (
        <Box sx={stepBodySx}>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            Pick one linked account to designate as the GuardDuty delegated
            administrator. This cannot be changed without tearing down and
            re-configuring.
          </Typography>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
            The admin account is permanent for this deployment. To change it later
            you must disable GuardDuty entirely and set it up again.
          </Alert>
          <Autocomplete
            options={accounts}
            getOptionLabel={(a) => `${a.accountName || a.name || a.accountId} (${a.accountId})`}
            value={accounts.find((a) => a.accountId === adminAccountId) || null}
            onChange={(_, v) => setAdminAccountId(v?.accountId || '')}
            renderInput={(params) => <TextField {...params} label="Admin account" />}
          />
        </Box>
      );
    }

    if (active === 1) {
      return (
        <Box sx={stepBodySx}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Home region</InputLabel>
            <Select
              value={homeRegion}
              label="Home region"
              onChange={(e) => {
                const v = e.target.value;
                setHomeRegion(v);
                if (!linkingRegions.includes(v)) setLinkingRegions([v, ...linkingRegions]);
              }}
            >
              {AWS_REGIONS.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </Select>
          </FormControl>
          <MultiSelectField
            label="Linking regions"
            options={AWS_REGIONS.map((r) => ({ value: r.value, label: r.label }))}
            value={linkingRegions}
            onChange={setLinkingRegions}
            lockedValues={homeRegion ? [homeRegion] : []}
          />
        </Box>
      );
    }

    if (active === 2) {
      return (
        <Box sx={stepBodySx}>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            Pick the protection plans to enable. All plans are enabled by default.
          </Typography>
          <Typography sx={{ ...sectionHeadingSx, fontSize: '1rem', mb: 1 }}>Protection plans</Typography>
          {FEATURE_OPTIONS.map((f) => (
            <FormControlLabel
              key={f.id}
              control={
                <Checkbox
                  sx={checkboxSx}
                  checked={features.includes(f.id)}
                  onChange={(e) => setFeatures((cur) =>
                    e.target.checked ? [...cur, f.id] : cur.filter((x) => x !== f.id))}
                />
              }
              label={f.label}
              sx={featureLabelSx}
            />
          ))}
        </Box>
      );
    }

    if (active === 3) {
      const memberOptions = accounts.filter((a) => a.accountId !== adminAccountId);
      return (
        <Box sx={stepBodySx}>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            Pick the member accounts to enroll. The admin is always included.
          </Typography>
          <MultiSelectField
            label="Member accounts"
            options={memberOptions.map((a) => ({
              value: a.accountId,
              label: `${a.accountName || a.name || a.accountId} (${a.accountId})`,
            }))}
            value={memberAccountIds}
            onChange={setMemberAccountIds}
          />
        </Box>
      );
    }

    if (active === 4) {
      return (
        <Box sx={stepBodySx}>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            Each member account needs its AWS root email to be invited. Emails are
            fetched automatically where possible; enter any that could not be resolved.
          </Typography>
          {resolvingEmails && <PortalLoading label="Fetching root emails…" color={GD} py={4} />}
          {!resolvingEmails && emailRows.length === 0 && (
            <Typography sx={{ ...emptyTextSx, fontSize: '0.875rem' }}>
              No member accounts selected — go back to Members and pick at least one.
            </Typography>
          )}
          <Stack spacing={2}>
            {emailRows.map((row) => {
              const acct = accounts.find((a) => a.accountId === row.accountId);
              const acctLabel = acct
                ? `${acct.accountName || acct.name || row.accountId} (${row.accountId})`
                : row.accountId;
              return (
              <Stack
                key={row.accountId}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Typography
                  sx={{
                    fontFamily: FONT_BODY,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: CK.navy,
                    minWidth: { xs: 0, sm: 260 },
                    wordBreak: 'break-word',
                  }}
                >
                  {acctLabel}
                </Typography>
                {row.resolved ? (
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.green, wordBreak: 'break-word' }}>
                    {row.email} (auto)
                  </Typography>
                ) : (
                  <TextField
                    size="small"
                    required
                    label="Root email"
                    sx={{ flex: 1, minWidth: 0 }}
                    value={manualEmails[row.accountId] || ''}
                    onChange={(e) => setManualEmails((m) => ({ ...m, [row.accountId]: e.target.value }))}
                    helperText={row.reason || 'Auto-fetch failed — enter this account’s AWS root email'}
                    error={!(manualEmails[row.accountId] || '').trim()}
                  />
                )}
              </Stack>
              );
            })}
          </Stack>
          {error && <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>{error}</Alert>}
        </Box>
      );
    }

    // review
    return (
      <Box sx={stepBodySx}>
        <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Review</Typography>
        <Typography sx={{ ...bodyTextSx, mb: 2 }}>
          Check the deployment below, then enable GuardDuty.
        </Typography>
        <Box sx={insetPanelSx}>
          <ReviewRow label="Admin account" value={adminAccountId} />
          <ReviewRow label="Home region" value={homeRegion} />
          <ReviewRow label="Linking regions" value={linkingRegions.join(', ')} />
          <ReviewRow label="Protection plans" value={`${features.length} enabled`} />
          <ReviewRow label="Member accounts" value={`${memberAccountIds.length + 1} (including admin)`} />
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>{error}</Alert>}
      </Box>
    );
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={<GppMaybeIcon sx={{ fontSize: 24 }} />}
          title="Set up GuardDuty"
          subtitle="Enable Amazon GuardDuty threat detection across your organization"
          color={GD}
        />
      </motion.div>
      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={sectionCardSx}>
        {priorFailure && !config && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: '8px' }}>
            The previous setup attempt failed: {priorFailure}. Submitting again will clear that state and retry.
          </Alert>
        )}
        <Stepper
          alternativeLabel
          activeStep={active}
          sx={{
            mb: 3,
            overflowX: 'auto',
            pb: 1,
            '& .MuiStep-root': { minWidth: 96 },
            '& .MuiStepIcon-root.Mui-active': { color: GD },
            '& .MuiStepIcon-root.Mui-completed': { color: GD },
            '& .MuiStepLabel-label': { fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.body },
            '& .MuiStepLabel-label.Mui-active': { fontWeight: 600, color: CK.navy },
            '& .MuiStepLabel-label.Mui-completed': { fontWeight: 600, color: CK.navy },
          }}
        >
          {steps.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
        </Stepper>
        {body()}
        {!config && (
          <Box sx={{ ...actionRowSx, justifyContent: 'flex-end', mt: 3, gap: 1 }}>
            <Button disabled={active === 0} onClick={back} sx={textBtnSx}>Back</Button>
            {active < steps.length - 1 ? (
              <Button variant="contained" disabled={!canNext()} onClick={next} sx={primaryBtnSx}>Next</Button>
            ) : (
              <Button variant="contained" disabled={submitting} onClick={submit} sx={primaryBtnSx}>
                {submitting ? 'Enabling…' : 'Enable GuardDuty'}
              </Button>
            )}
          </Box>
        )}
      </Paper>
      </motion.div>
    </motion.div>
  );
}
